import { useEffect, useMemo, useState } from "react";
import {
  type GmailConfiguration,
  type GmailLabel,
} from "./GmailSourceSettings.types";
import { GmailSourceSettingsApi } from "./GmailSourceSettings.Api";
import { SourceTypeIds } from "../SourceTypeIds";
import {
  parseConnectionConfiguration,
  updateConnectionConfiguration,
} from "../source-settings/SourceSettingsConfiguration";
import type {
  SourceSettingsModel,
} from "../source-settings/SourceSettingsApiAccess";
import type { GmailSourceSettingsViewModel } from "./GmailSourceSettings.ViewModel.types";

const NONE_OPTION = "__none";

export const useGmailSourceSettingsViewModel = (
  model: SourceSettingsModel,
): GmailSourceSettingsViewModel => {
  const [labels, setLabels] = useState<GmailLabel[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [hasError, setHasError] = useState(false);

  useEffect(() => {
    const loadLabels = async (): Promise<void> => {
      if (
        !model.boardId ||
        !model.editingConnection.id ||
        !model.editingConnection.isAuthorised
      ) {
        setLabels([]);
        return;
      }

      if (!model.sourceSettingsApi) {
        setLabels([]);
        return;
      }

      const gmailApi =
        model.sourceSettingsApi.tryGetSourceApi<GmailSourceSettingsApi>(
          SourceTypeIds.Gmail,
        );
      if (!gmailApi) {
        setLabels([]);
        return;
      }

      setIsLoading(true);
      setHasError(false);
      try {
        const response = await gmailApi.getLabels(
          model.boardId,
          model.editingConnection.id,
        );
        setLabels(Array.isArray(response) ? response : []);
      } catch {
        setLabels([]);
        setHasError(true);
      } finally {
        setIsLoading(false);
      }
    };

    void loadLabels();
  }, [
    model.boardId,
    model.editingConnection.id,
    model.editingConnection.isAuthorised,
    model.sourceSettingsApi,
  ]);

  const configuration = useMemo(
    () => parseConnectionConfiguration<GmailConfiguration>(model.editingConnection.configuration),
    [model.editingConnection.configuration],
  );

  const selectedLabelId = useMemo(
    () => configuration.label?.id ?? NONE_OPTION,
    [configuration],
  );

  const isStarred = useMemo(() => configuration.starred ?? false, [configuration]);

  return {
    isVisible: model.editingConnection.isAuthorised,
    isLoading,
    hasError,
    labels,
    selectedLabelId,
    selectedLabelName:
      selectedLabelId === NONE_OPTION
        ? ""
        : labels.find((label) => label.id === selectedLabelId)?.name ||
          "",
    noneOptionValue: NONE_OPTION,
    isStarred,
    onToggleStarred: (checked: boolean) => {
      updateConnectionConfiguration<GmailConfiguration>(
        model.editingConnection,
        model.onUpdate,
        (cfg) => ({ ...cfg, starred: checked }),
      );
    },
    onSelectLabel: (optionValue?: string) => {
      const selectedId = String(optionValue || NONE_OPTION);
      updateConnectionConfiguration<GmailConfiguration>(
        model.editingConnection,
        model.onUpdate,
        (configuration) => {
          if (selectedId === NONE_OPTION) {
            return { ...configuration, label: undefined };
          }

          const selectedLabel = labels.find((label) => label.id === selectedId);
          if (!selectedLabel) {
            return configuration;
          }

          return {
            ...configuration,
            label: selectedLabel,
          };
        },
      );
    },
  };
};
